import { computed, ref, toValue } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { getRedemptions } from '~/services/apiMyAccount';
import { useAuthStore } from '~/stores/useAuthStore';
import { useProfileStore } from '~/stores/useProfileStore';
import { useTimer } from '~/composables/useTimer';
import usePaginator from '~/composables/usePaginator';
import { RedemptionsListItem } from '~/models/Redemptions/RedemptionsListItem';
import { RedemptionsListFilters } from '~/models/Redemptions/RedemptionsListFilters';
import FeatureFlagService from '~/services/FeatureFlagService';

export const useRedemptionStore = defineStore(
  'REDEMPTION_STORE',
  () => {
    const AUTH_STORE = useAuthStore();
    const PROFILE_STORE = useProfileStore();

    const { profile } = storeToRefs(PROFILE_STORE);
    const { resellerDocument } = storeToRefs(AUTH_STORE);

    const timer = useTimer();
    const paginator = usePaginator();
    const { pagination, CallResetPagination, CallSetPagination, CallPaginateAndFetch } = paginator;

    const redemptionList = ref<RedemptionsListItem[] | null>(null);
    const filters = ref<RedemptionsListFilters>({
      status: 0,
      date: 5,
    });
    const loadingRedemptionList = ref<boolean>(true);
    const errorMessage = ref();

    const isLoadingRedemptionList = computed(() => loadingRedemptionList.value);
    const getRedemptionList = computed(() => redemptionList.value);

    const isActiveFeature = computed(() => {
      if (FeatureFlagService !== null) {
        const { currentEconomicGroupMember } = AUTH_STORE.session;
        const whiteList = FeatureFlagService?.findByFlagName('WhiteListCommissions');

        return (
          FeatureFlagService?.isOn('CommissionsFeaturesAvailable') ||
          (Array.isArray(whiteList?.defaultValue) && whiteList.defaultValue.includes(currentEconomicGroupMember))
        );
      }

      return false;
    });

    function setRedemptionList(data: RedemptionsListItem[] | null) {
      if (!data) redemptionList.value = [];
      else redemptionList.value = [...data];
    }

    function resetFilters() {
      filters.value = { status: 0, date: 5 };
      CallResetPagination();
      dispatchGetRedemptions(true);
    }

    function adaptParams() {
      return {
        ...RedemptionsListFilters.toDto(toValue(filters) as RedemptionsListFilters),
        currentPage: pagination.value.currentPage,
        pageSize: pagination.value.pageSize,
      };
    }

    async function dispatchGetRedemptions(force: boolean = false) {
      if (!isActiveFeature.value) return;
      if (profile.value.isCommercial) return;
      if (!timer.isExpired.value && !force) return;

      loadingRedemptionList.value = true;

      await getRedemptions(resellerDocument.value, adaptParams()).then(({items, pagination, success}) => {

        if (!success) {
          setRedemptionList(null);
          timer.resetTimer();
          CallResetPagination();
          loadingRedemptionList.value = false;
          return;
        }

        if (Array.isArray(items)) {
          setRedemptionList(items.map((item: any) => new RedemptionsListItem(item)));
        } else {
          setRedemptionList([]);
        }
        CallSetPagination(pagination);
        timer.startTimer();
        loadingRedemptionList.value = false;
      });
    }

    function $reset() {
      setRedemptionList(null);
      resetFilters();
    }

    return {
      isActiveFeature,
      isLoadingRedemptionList,
      loadingRedemptionList,
      errorMessage: computed(() => errorMessage.value),
      redemptionList: getRedemptionList,
      redemptionListFilters: filters,
      redemptionPagination: pagination,
      setRedemptionList,
      resetFilters,
      dispatchGetRedemptions,
      onSessionClose: $reset,
      onChangeEconomicGroup: () => dispatchGetRedemptions(true),
      resetRedemptionListFilters: resetFilters,
      paginate: (data: any) => CallPaginateAndFetch(data, () => dispatchGetRedemptions(true)),
      dispatchFilterRedemptions: () => dispatchGetRedemptions(true),
      $reset,
    };
  },
  { persist: true },
);
